import { api } from "../AxiosInstance";
import { IUser } from "../interfaces/user.interface";
import JobService from "./JobService";
import FederationService from "./FederationService";
import TransporterService from "./TransporterService";
import TruckService from "./Truck.service";
import UserManagementService from "./UserManagementService";
import TripHistoryService from "./TripHistoryService";

export interface RecentJob {
  id: string | number;
  jobNo: string;
  containerNo: string;
  containerSize: string;
  truck: string;
  source: string;
  destination: string;
  status: string;
  updatedAt: string;
}

export interface DashboardData {
  jobStats: {
    totalJobs: number;
    activeJobs: number;
    completedJobs: number;
    cancelledJobs: number;
    containers: number;
    count20: number;
    count40: number;
    count45: number;
  };

  jobStatusCounts: {
    total: number;
    initiated: number;
    assigned: number;
    started: number;
    inProgress: number;
    completed: number;
    cancelled: number;
  };

  recentJobs: RecentJob[];

  tripStats: {
    totalTrips: number;
    activeTrips: number;
    completedTrips: number;
    cancelledTrips: number;
    containers: number;
    count20: number;
    count40: number;
    count45: number;
  };

  federationStats?: {
    totalFederations: number;
    activeFederations: number;
    totalTransporters: number;
    totalJobs: number;
  };

  transporterStats?: {
    totalTransporters: number;
    activeTransporters: number;
  };

  truckStats?: {
    totalTrucks: number;
    activeTrucks: number;
    inactiveTrucks: number;
    maintenanceTrucks: number;
  };

  driverStats?: {
    totalDrivers: number;
    availableDrivers: number;
    assignedDrivers: number;
  };

  userStats?: {
    totalUsers: number;
    activeUsers: number;
    pendingUsers: number;
    usersByType: {
      ENTERPRISE: number;
      FEDERATION: number;
      TRANSPORTER: number;
    };
  };

  recentActivity: ActivityItem[];
}

export interface ActivityItem {
  id: string;
  type:
    | "JOB_CREATED"
    | "JOB_COMPLETED"
    | "TRIP_STARTED"
    | "TRIP_COMPLETED"
    | "USER_ADDED";
  title: string;
  description: string;
  timestamp: string;
  userId: string;
  userName: string;
  metadata?: Record<string, any>;
}

const containerSizeLabel = (size: string): string => {
  switch (size) {
    case "TWENTY":     return "20 ft";
    case "FORTY":      return "40 ft";
    case "FORTY_FIVE": return "45 ft";
    default:           return size || "-";
  }
};

const mapRecentJobs = (jobs: any[]): RecentJob[] =>
  jobs.slice(0, 10).map((j: any) => {
    const breakup = j.jobBreakups?.[0];
    const detail  = breakup?.jobBreakUpDetails?.[0];
    return {
      id:            j.id || j.jobId || "",
      jobNo:         j.jobCode || j.jobNo || String(j.id || ""),
      containerNo:   detail?.containerNumber || detail?.containerNo || "-",
      containerSize: containerSizeLabel(breakup?.containerSize),
      truck:         j.truckRegistrationNumber || j.truck?.registrationNumber || "-",
      source:        j.pickupLocationName || j.source || j.pickupLocation?.name || "-",
      destination:   j.dropLocationName   || j.destination || j.dropLocation?.name  || "-",
      status:        j.jobStatus || "-",
      updatedAt:     j.completedAt || j.updatedAt || j.createdAt || "",
    };
  });

const computeStatusCounts = (jobs: any[]) => ({
  total:      jobs.length,
  initiated:  jobs.filter((j: any) => j.jobStatus === "INITIATED").length,
  assigned:   jobs.filter((j: any) => j.jobStatus === "ASSIGNED").length,
  started:    jobs.filter((j: any) => j.jobStatus === "STARTED").length,
  inProgress: jobs.filter((j: any) => j.jobStatus === "IN_PROGRESS").length,
  completed:  jobs.filter((j: any) => j.jobStatus === "COMPLETED").length,
  cancelled:  jobs.filter((j: any) => j.jobStatus === "CANCELLED").length,
});

const DashboardService = {
  getDashboardData: async (userProfile: IUser): Promise<DashboardData> => {
    const role = userProfile?.role?.name;
    console.log("DashboardService.getDashboardData called with role:", role);

    switch (role) {
      case "ROLE_CLIENT_ADMIN":
        console.log("Calling getEnterpriseDashboardData");
        return await DashboardService.getEnterpriseDashboardData(userProfile);
      case "ROLE_FEDERATION_ADMIN":
        console.log("Calling getFederationDashboardData");
        return await DashboardService.getFederationDashboardData(userProfile);
      case "ROLE_TRANSPORTER_ADMIN":
        console.log("Calling getTransporterDashboardData");
        return await DashboardService.getTransporterDashboardData(userProfile);
      default:
        console.log("Invalid user role:", role);
        throw new Error("Invalid user role");
    }
  },

  getEnterpriseDashboardData: async (
    userProfile?: IUser
  ): Promise<DashboardData> => {
    try {
      console.log("getEnterpriseDashboardData called");

      // Fetch data using the new API endpoints
      const results = await Promise.allSettled([
        // Enterprise federations
        api.get("/enterprise/federations"),
        // Enterprise transporters
        api.get("/enterprise/transporters"),
        // Job history
        api.get("/job-history"),
        // Trip history
        api.get("/job-history/trips"),
        // Trip statistics
        api.get("/job-history/trip-stats"),
        // Users
        api.get("/users"),
      ]);

      // Extract data with proper error handling
      const federationsResponse =
        results[0].status === "fulfilled" ? results[0].value : null;
      const transportersResponse =
        results[1].status === "fulfilled" ? results[1].value : null;
      const jobsResponse =
        results[2].status === "fulfilled" ? results[2].value : null;
      const tripsResponse =
        results[3].status === "fulfilled" ? results[3].value : null;
      const tripStatsResponse =
        results[4].status === "fulfilled" ? results[4].value : null;
      const usersResponse =
        results[5].status === "fulfilled" ? results[5].value : null;

      console.log("Enterprise Dashboard API Responses:", {
        federations: federationsResponse?.data,
        transporters: transportersResponse?.data,
        jobs: jobsResponse?.data,
        trips: tripsResponse?.data,
        tripStats: tripStatsResponse?.data,
        users: usersResponse?.data,
      });

      // Extract data arrays
      const federations = federationsResponse?.data?.data || [];
      const transporters = transportersResponse?.data?.data || [];
      const jobs = jobsResponse?.data?.data?.content || [];
      const trips = tripsResponse?.data?.data?.content || [];
      const tripStats = tripStatsResponse?.data?.data || {};
      const users = usersResponse?.data?.content || [];

      // Process federations data
      const totalFederations = federations.length;
      const activeFederations = federations.filter(
        (f: any) => f.status === "ACTIVE"
      ).length;

      // Process transporters data
      const totalTransporters = transporters.length;
      const activeTransporters = transporters.filter(
        (t: any) => t.status === "ACTIVE"
      ).length;

      // Process jobs data
      const totalJobs = jobs.length;
      const activeJobs = jobs.filter(
        (j: any) =>
          j.jobStatus === "STARTED" ||
          j.jobStatus === "ASSIGNED" ||
          j.jobStatus === "IN_PROGRESS"
      ).length;
      const completedJobs = jobs.filter(
        (j: any) => j.jobStatus === "COMPLETED"
      ).length;
      const cancelledJobs = jobs.filter(
        (j: any) => j.jobStatus === "CANCELLED"
      ).length;

      // Calculate container counts from jobBreakups
      const containers = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
          }, 0) || 0)
        );
      }, 0);

      const count20 = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            if (breakup.containerSize === "TWENTY") {
              return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
            }
            return breakupTotal;
          }, 0) || 0)
        );
      }, 0);

      const count40 = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            if (breakup.containerSize === "FORTY") {
              return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
            }
            return breakupTotal;
          }, 0) || 0)
        );
      }, 0);

      const count45 = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            if (breakup.containerSize === "FORTY_FIVE") {
              return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
            }
            return breakupTotal;
          }, 0) || 0)
        );
      }, 0);

      // Process trip statistics
      const tripStatsData = {
        totalTrips: tripStats.trips || trips.length,
        activeTrips: trips.filter(
          (t: any) =>
            t.tripStatus === "IN_TRANSIT" ||
            t.tripStatus === "STARTED" ||
            t.tripStatus === "IN_PROGRESS"
        ).length,
        completedTrips: trips.filter(
          (t: any) => t.tripStatus === "COMPLETED" || t.tripStatus === "DROPPED"
        ).length,
        cancelledTrips: trips.filter((t: any) => t.tripStatus === "CANCELLED")
          .length,
        containers: tripStats.containers || 0,
        count20: tripStats.count20 || 0,
        count40: tripStats.count40 || 0,
        count45: tripStats.count45 || 0,
      };

      // Process users data
      const totalUsers = users.length;
      const activeUsers = users.filter((u: any) => !u.disabled).length;
      const pendingUsers = users.filter((u: any) => u.disabled).length;

      const usersByType = {
        ENTERPRISE: users.filter(
          (u: any) => u.role?.name === "ROLE_CLIENT_ADMIN"
        ).length,
        FEDERATION: users.filter(
          (u: any) => u.role?.name === "ROLE_FEDERATION_ADMIN"
        ).length,
        TRANSPORTER: users.filter(
          (u: any) => u.role?.name === "ROLE_TRANSPORTER_ADMIN"
        ).length,
      };

      const dashboardData = {
        jobStats: {
          totalJobs,
          activeJobs,
          completedJobs,
          cancelledJobs,
          containers,
          count20,
          count40,
          count45,
        },
        jobStatusCounts: computeStatusCounts(jobs),
        recentJobs: mapRecentJobs(jobs),
        tripStats: tripStatsData,
        federationStats: {
          totalFederations,
          activeFederations,
          totalTransporters,
          totalJobs,
        },
        userStats: {
          totalUsers,
          activeUsers,
          pendingUsers,
          usersByType,
        },
        recentActivity: await DashboardService.getRecentActivity("enterprise"),
      };

      console.log("Final Enterprise Dashboard Data:", dashboardData);
      return dashboardData;
    } catch (error) {
      console.error("Error fetching enterprise dashboard data:", error);
      return DashboardService.getDefaultDashboardData();
    }
  },

  getFederationDashboardData: async (
    userProfile?: IUser
  ): Promise<DashboardData> => {
    try {
      console.log("getFederationDashboardData called");

      // For federation admin, we'll use the same endpoints but filter by federation
      const results = await Promise.allSettled([
        // Federation transporters (filtered by federation)
        api.get("/enterprise/transporters"),
        // Job history (filtered by federation)
        api.get("/job-history"),
        // Trip history (filtered by federation)
        api.get("/job-history/trips"),
        // Trip statistics (filtered by federation)
        api.get("/job-history/trip-stats"),
        // Drivers (using existing service)
        TransporterService.getAllDrivers(),
      ]);

      const transportersResponse =
        results[0].status === "fulfilled" ? results[0].value : null;
      const jobsResponse =
        results[1].status === "fulfilled" ? results[1].value : null;
      const tripsResponse =
        results[2].status === "fulfilled" ? results[2].value : null;
      const tripStatsResponse =
        results[3].status === "fulfilled" ? results[3].value : null;
      const driversResponse =
        results[4].status === "fulfilled" ? results[4].value : null;

      console.log("Federation Dashboard API Responses:", {
        transporters: transportersResponse?.data,
        jobs: jobsResponse?.data,
        trips: tripsResponse?.data,
        tripStats: tripStatsResponse?.data,
        drivers: driversResponse?.data,
      });

      // Extract data arrays
      const transporters = transportersResponse?.data?.data || [];
      const jobs = jobsResponse?.data?.data?.content || [];
      const trips = tripsResponse?.data?.data?.content || [];
      const tripStats = tripStatsResponse?.data?.data || {};
      const drivers =
        driversResponse?.data?.content || driversResponse?.data?.data || [];

      // Process transporters data
      const totalTransporters = transporters.length;
      const activeTransporters = transporters.filter(
        (t: any) => t.status === "ACTIVE"
      ).length;

      // Process jobs data
      const totalJobs = jobs.length;
      const activeJobs = jobs.filter(
        (j: any) =>
          j.jobStatus === "STARTED" ||
          j.jobStatus === "ASSIGNED" ||
          j.jobStatus === "IN_PROGRESS"
      ).length;
      const completedJobs = jobs.filter(
        (j: any) => j.jobStatus === "COMPLETED"
      ).length;
      const cancelledJobs = jobs.filter(
        (j: any) => j.jobStatus === "CANCELLED"
      ).length;

      // Calculate container counts
      const containers = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
          }, 0) || 0)
        );
      }, 0);

      const count20 = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            if (breakup.containerSize === "TWENTY") {
              return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
            }
            return breakupTotal;
          }, 0) || 0)
        );
      }, 0);

      const count40 = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            if (breakup.containerSize === "FORTY") {
              return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
            }
            return breakupTotal;
          }, 0) || 0)
        );
      }, 0);

      const count45 = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            if (breakup.containerSize === "FORTY_FIVE") {
              return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
            }
            return breakupTotal;
          }, 0) || 0)
        );
      }, 0);

      // Process trip statistics
      const tripStatsData = {
        totalTrips: tripStats.trips || trips.length,
        activeTrips: trips.filter(
          (t: any) =>
            t.tripStatus === "IN_TRANSIT" ||
            t.tripStatus === "STARTED" ||
            t.tripStatus === "IN_PROGRESS"
        ).length,
        completedTrips: trips.filter(
          (t: any) => t.tripStatus === "COMPLETED" || t.tripStatus === "DROPPED"
        ).length,
        cancelledTrips: trips.filter((t: any) => t.tripStatus === "CANCELLED")
          .length,
        containers: tripStats.containers || 0,
        count20: tripStats.count20 || 0,
        count40: tripStats.count40 || 0,
        count45: tripStats.count45 || 0,
      };

      // Process drivers data
      const totalDrivers = drivers.length;
      const availableDrivers = drivers.filter(
        (d: any) => d.available || d.status === "AVAILABLE"
      ).length;
      const assignedDrivers = totalDrivers - availableDrivers;

      return {
        jobStats: {
          totalJobs,
          activeJobs,
          completedJobs,
          cancelledJobs,
          containers,
          count20,
          count40,
          count45,
        },
        jobStatusCounts: computeStatusCounts(jobs),
        recentJobs: mapRecentJobs(jobs),
        tripStats: tripStatsData,
        transporterStats: {
          totalTransporters,
          activeTransporters,
        },
        driverStats: {
          totalDrivers,
          availableDrivers,
          assignedDrivers,
        },
        recentActivity: await DashboardService.getRecentActivity("federation"),
      };
    } catch (error) {
      console.error("Error fetching federation dashboard data:", error);
      return DashboardService.getDefaultDashboardData();
    }
  },

  getTransporterDashboardData: async (
    userProfile?: IUser
  ): Promise<DashboardData> => {
    try {
      console.log("getTransporterDashboardData called");

      // For transporter admin, we'll use the same endpoints but filter by transporter
      const results = await Promise.allSettled([
        // Transporter trucks
        TruckService.getTransporterTrucks({}),
        // Transporter drivers
        TransporterService.getAllDrivers(),
        // Job history (filtered by transporter)
        api.get("/job-history"),
        // Trip history (filtered by transporter)
        api.get("/job-history/trips"),
        // Trip statistics (filtered by transporter)
        api.get("/job-history/trip-stats"),
      ]);

      const trucksResponse =
        results[0].status === "fulfilled" ? results[0].value : null;
      const driversResponse =
        results[1].status === "fulfilled" ? results[1].value : null;
      const jobsResponse =
        results[2].status === "fulfilled" ? results[2].value : null;
      const tripsResponse =
        results[3].status === "fulfilled" ? results[3].value : null;
      const tripStatsResponse =
        results[4].status === "fulfilled" ? results[4].value : null;

      console.log("Transporter Dashboard API Responses:", {
        trucks: trucksResponse?.data,
        drivers: driversResponse?.data,
        jobs: jobsResponse?.data,
        trips: tripsResponse?.data,
        tripStats: tripStatsResponse?.data,
      });

      // Extract data arrays
      const trucks = trucksResponse?.data?.content || [];
      const drivers =
        driversResponse?.data?.content || driversResponse?.data?.data || [];
      const jobs = jobsResponse?.data?.data?.content || [];
      const trips = tripsResponse?.data?.data?.content || [];
      const tripStats = tripStatsResponse?.data?.data || {};

      // Process trucks data
      const totalTrucks = trucks.length;
      const activeTrucks = trucks.filter(
        (t: any) => t.status === "ACTIVE"
      ).length;
      const inactiveTrucks = trucks.filter(
        (t: any) => t.status === "INACTIVE"
      ).length;
      const maintenanceTrucks = trucks.filter(
        (t: any) => t.status === "MAINTENANCE"
      ).length;

      // Process jobs data
      const totalJobs = jobs.length;
      const activeJobs = jobs.filter(
        (j: any) =>
          j.jobStatus === "STARTED" ||
          j.jobStatus === "ASSIGNED" ||
          j.jobStatus === "IN_PROGRESS"
      ).length;
      const completedJobs = jobs.filter(
        (j: any) => j.jobStatus === "COMPLETED"
      ).length;
      const cancelledJobs = jobs.filter(
        (j: any) => j.jobStatus === "CANCELLED"
      ).length;

      // Calculate container counts
      const containers = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
          }, 0) || 0)
        );
      }, 0);

      const count20 = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            if (breakup.containerSize === "TWENTY") {
              return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
            }
            return breakupTotal;
          }, 0) || 0)
        );
      }, 0);

      const count40 = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            if (breakup.containerSize === "FORTY") {
              return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
            }
            return breakupTotal;
          }, 0) || 0)
        );
      }, 0);

      const count45 = jobs.reduce((total: number, job: any) => {
        return (
          total +
          (job.jobBreakups?.reduce((breakupTotal: number, breakup: any) => {
            if (breakup.containerSize === "FORTY_FIVE") {
              return breakupTotal + (breakup.jobBreakUpDetails?.length || 0);
            }
            return breakupTotal;
          }, 0) || 0)
        );
      }, 0);

      // Process trip statistics
      const tripStatsData = {
        totalTrips: tripStats.trips || trips.length,
        activeTrips: trips.filter(
          (t: any) =>
            t.tripStatus === "IN_TRANSIT" ||
            t.tripStatus === "STARTED" ||
            t.tripStatus === "IN_PROGRESS"
        ).length,
        completedTrips: trips.filter(
          (t: any) => t.tripStatus === "COMPLETED" || t.tripStatus === "DROPPED"
        ).length,
        cancelledTrips: trips.filter((t: any) => t.tripStatus === "CANCELLED")
          .length,
        containers: tripStats.containers || 0,
        count20: tripStats.count20 || 0,
        count40: tripStats.count40 || 0,
        count45: tripStats.count45 || 0,
      };

      // Process drivers data
      const totalDrivers = drivers.length;
      const availableDrivers = drivers.filter(
        (d: any) => d.available || d.status === "AVAILABLE"
      ).length;
      const assignedDrivers = totalDrivers - availableDrivers;

      return {
        jobStats: {
          totalJobs,
          activeJobs,
          completedJobs,
          cancelledJobs,
          containers,
          count20,
          count40,
          count45,
        },
        jobStatusCounts: computeStatusCounts(jobs),
        recentJobs: mapRecentJobs(jobs),
        tripStats: tripStatsData,
        truckStats: {
          totalTrucks,
          activeTrucks,
          inactiveTrucks,
          maintenanceTrucks,
        },
        driverStats: {
          totalDrivers,
          availableDrivers,
          assignedDrivers,
        },
        recentActivity: await DashboardService.getRecentActivity("transporter"),
      };
    } catch (error) {
      console.error("Error fetching transporter dashboard data:", error);
      return DashboardService.getDefaultDashboardData();
    }
  },

  getDefaultDashboardData: (): DashboardData => {
    return {
      jobStats: {
        totalJobs: 0,
        activeJobs: 0,
        completedJobs: 0,
        cancelledJobs: 0,
        containers: 0,
        count20: 0,
        count40: 0,
        count45: 0,
      },
      jobStatusCounts: {
        total: 0,
        initiated: 0,
        assigned: 0,
        started: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
      },
      recentJobs: [],
      tripStats: {
        totalTrips: 0,
        activeTrips: 0,
        completedTrips: 0,
        cancelledTrips: 0,
        containers: 0,
        count20: 0,
        count40: 0,
        count45: 0,
      },
      federationStats: {
        totalFederations: 0,
        activeFederations: 0,
        totalTransporters: 0,
        totalJobs: 0,
      },
      transporterStats: {
        totalTransporters: 0,
        activeTransporters: 0,
      },
      truckStats: {
        totalTrucks: 0,
        activeTrucks: 0,
        inactiveTrucks: 0,
        maintenanceTrucks: 0,
      },
      driverStats: {
        totalDrivers: 0,
        availableDrivers: 0,
        assignedDrivers: 0,
      },
      userStats: {
        totalUsers: 0,
        activeUsers: 0,
        pendingUsers: 0,
        usersByType: {
          ENTERPRISE: 0,
          FEDERATION: 0,
          TRANSPORTER: 0,
        },
      },
      recentActivity: [],
    };
  },

  getRecentActivity: async (
    scope: "enterprise" | "federation" | "transporter"
  ): Promise<ActivityItem[]> => {
    try {
      // This would typically call an API endpoint for recent activity
      // For now, returning mock data
      const mockActivities: ActivityItem[] = [
        {
          id: "1",
          type: "JOB_CREATED",
          title: "New Job Created",
          description: "Job EPFEB07 has been created",
          timestamp: new Date().toISOString(),
          userId: "1",
          userName: "Admin User",
          metadata: { jobCode: "EPFEB07" },
        },
        {
          id: "2",
          type: "TRIP_STARTED",
          title: "Trip Started",
          description: "Trip for container EMCU1234567 has started",
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          userId: "2",
          userName: "Driver User",
          metadata: { containerNumber: "EMCU1234567" },
        },
      ];

      return mockActivities;
    } catch (error) {
      console.error("Error fetching recent activity:", error);
      return [];
    }
  },

  // Get role-based metrics for dashboard display
  getRoleBasedMetrics: (
    userProfile: IUser | null,
    dashboardData: DashboardData | null
  ) => {
    if (!dashboardData || !userProfile) return [];

    const role = userProfile?.role?.name;

    switch (role) {
      case "ROLE_CLIENT_ADMIN":
        return [
          {
            name: "Total Federations",
            value: dashboardData.federationStats?.totalFederations || 0,
            valueColor: "#007bff",
          },
          {
            name: "Active Transporters",
            value: dashboardData.transporterStats?.activeTransporters || 0,
            valueColor: "#28a745",
          },
          {
            name: "Total Jobs",
            value: dashboardData.jobStats.totalJobs,
            valueColor: "#59585c",
          },
          {
            name: "Active Trips",
            value: dashboardData.tripStats.activeTrips,
            valueColor: "#17a2b8",
          },
          {
            name: "Completed Trips",
            value: dashboardData.tripStats.completedTrips,
            valueColor: "#6f42c1",
          },
          {
            name: "Total Users",
            value: dashboardData.userStats?.totalUsers || 0,
            valueColor: "#fd7e14",
          },
        ];

      case "ROLE_FEDERATION_ADMIN":
        return [
          {
            name: "Total Transporters",
            value: dashboardData.transporterStats?.totalTransporters || 0,
            valueColor: "#007bff",
          },
          {
            name: "Active Drivers",
            value: dashboardData.driverStats?.availableDrivers || 0,
            valueColor: "#28a745",
          },
          {
            name: "Total Jobs",
            value: dashboardData.jobStats.totalJobs,
            valueColor: "#59585c",
          },
          {
            name: "Active Trips",
            value: dashboardData.tripStats.activeTrips,
            valueColor: "#17a2b8",
          },
          {
            name: "Completed Trips",
            value: dashboardData.tripStats.completedTrips,
            valueColor: "#6f42c1",
          },
        ];

      case "ROLE_TRANSPORTER_ADMIN":
        return [
          {
            name: "Total Trucks",
            value: dashboardData.truckStats?.totalTrucks || 0,
            valueColor: "#007bff",
          },
          {
            name: "Active Trucks",
            value: dashboardData.truckStats?.activeTrucks || 0,
            valueColor: "#28a745",
          },
          {
            name: "Total Drivers",
            value: dashboardData.driverStats?.totalDrivers || 0,
            valueColor: "#ffc107",
          },
          {
            name: "Active Trips",
            value: dashboardData.tripStats.activeTrips,
            valueColor: "#17a2b8",
          },
          {
            name: "Completed Trips",
            value: dashboardData.tripStats.completedTrips,
            valueColor: "#6f42c1",
          },
        ];

      default:
        return [];
    }
  },
};

export default DashboardService;
